// components/ConsentCheckbox.jsx — Anamoria SPA
// v1.2 — Consent copy (Oct 4, 2026; copy v0.4): notice body resolves {owner} -> ownerName at render (never shows the raw token); callers pass ownerName. Display-only; stored body + hash unchanged.
// v1.1 — Consent copy/UX tweak (Oct 4, 2026): checkbox visual size 20->16px; optional boxOnRight layout (label left, box right) for the feed switch. Label/behaviour unchanged.
// v1.0 — Consent PR E1 (Oct 4, 2026). The ONE reusable consent checkbox (plan v0.26 §6.1; PR E spec v0.2 §2 E1).
//
//   - Fetches the STORED notice from the server (GET /notices/{docType}, PR B) on mount. The wording shown
//     is the wording recorded — D14 a. 404 NOTICE_NOT_FOUND (no text stored yet) or any other failure =>
//     renders NOTHING (fail closed, plan §6.5: no stored notice -> the checkbox is not offered).
//   - Never pre-ticked (ICO: "Silence, pre-ticked boxes or inactivity should not therefore constitute
//     consent"). Equal visual weight with the controls around it; the label is the click target, >= 24px
//     (WCAG 2.2 2.5.8 target size); unticking is one step (ICO: as easy to withdraw as to give).
//   - "What this means" discloses the stored notice body verbatim (title + paragraphs as stored).
//   - onChange(checked, shownHash): the hash of the wording the person actually saw (notice contentHash),
//     which the caller sends as *ShownHash with every grant (B2 — the server refuses a stale hash).
//   - Props: api (object with get(path)), docType, label, checked, onChange, disabled, id.
//
// Self-contained inline styles on purpose (as ContributorSaveActions): no shared CSS-module edits.
import { useEffect, useState } from 'react';

export default function ConsentCheckbox({ api, docType, label, checked = false, onChange, disabled = false, id, boxOnRight = false, ownerName }) {
  const [notice, setNotice] = useState(null);     // { version, body, contentHash } | null
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setNotice(null);
    if (!api || !docType) return undefined;
    api.get(`/notices/${encodeURIComponent(docType)}`)
      .then((data) => { if (!cancelled && data && typeof data.body === 'string' && data.contentHash) setNotice(data); })
      .catch(() => { /* 404 NOTICE_NOT_FOUND or any error: stay hidden (fail closed) */ });
    return () => { cancelled = true; };
  }, [api, docType]);

  if (!notice) return null;

  const inputId = id || `consent-${docType}`;
  const disclosureId = `${inputId}-notice`;
  const ownerText = ownerName || 'the space owner';
  const paragraphs = notice.body.replace(/\{owner\}/g, ownerText).split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  return (
    <div style={boxOnRight ? { ...S.wrap, marginTop: 0 } : S.wrap} data-consent-doc-type={docType} data-consent-version={notice.version}>
      {boxOnRight ? (
        <div style={S.rowRight}>
          <label htmlFor={inputId} style={S.labelGroup}>
            <input id={inputId} type="checkbox" checked={checked === true} disabled={disabled} onChange={(e) => onChange?.(e.target.checked, notice.contentHash)} style={S.boxRight} />
            <span style={S.label}>{label}</span>
          </label>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={disclosureId} style={S.linkInline}>What this means</button>
        </div>
      ) : (
        <>
          <label htmlFor={inputId} style={S.row}>
            <input id={inputId} type="checkbox" checked={checked === true} disabled={disabled} onChange={(e) => onChange?.(e.target.checked, notice.contentHash)} style={S.box} />
            <span style={S.label}>{label}</span>
          </label>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={disclosureId} style={{ ...S.link, paddingLeft: 30 }}>What this means</button>
        </>
      )}
      {open && (
        <div id={disclosureId} style={{ ...S.notice, marginLeft: boxOnRight ? 0 : 30 }}>
          {paragraphs.map((p, i) => <p key={i} style={S.p}>{p}</p>)}
        </div>
      )}
    </div>
  );
}

const S = {
  wrap: { margin: '12px 0 4px' },
  row: { display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', minHeight: 24 },
  rowRight: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 10, minHeight: 24 },
  labelGroup: { display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', minHeight: 24 },
  linkInline: { background: 'none', border: 'none', padding: 0, fontSize: 13, color: 'var(--color-accent, #5b7f6a)', textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 24 },
  box: { width: 16, height: 16, margin: '3px 0 0', flex: '0 0 auto', accentColor: 'var(--color-accent, #5b7f6a)' },
  boxRight: { width: 14, height: 14, margin: 0, flex: '0 0 auto', accentColor: 'var(--color-accent, #5b7f6a)' },
  label: { fontSize: 15, lineHeight: 1.45, color: 'var(--color-text-primary, #2d3436)' },
  link: { background: 'none', border: 'none', padding: '6px 0 0 30px', fontSize: 13, color: 'var(--color-accent, #5b7f6a)', textDecoration: 'underline', cursor: 'pointer', minHeight: 24 },
  notice: { margin: '8px 0 0 30px', padding: '12px 14px', borderRadius: 10, background: 'var(--color-bg, #faf9f7)', border: '1px solid #e0ddd8' },
  p: { margin: '0 0 8px', fontSize: 14, lineHeight: 1.5, color: 'var(--color-text-primary, #2d3436)' },
};
